import { type BadgeDef, newlyEarnedBadges } from "./badges";
import { applyEventToQuest, type QuestDef } from "./quests";
import { computeStreak, dayKeyUTC } from "./streaks";
import { DEFAULT_CURVE, levelForXp, type XpCurve } from "./xp";
import type {
  Awards,
  DayKey,
  GameEvent,
  PlayerState,
  StreakState,
} from "./types";

/**
 * Motor de gamificación event-driven. Es un REDUCER puro:
 *   (estado, evento[]) → (estado nuevo, recompensas)
 * Agnóstico del dominio: las reglas (XP por evento, líneas de racha, badges,
 * quests) llegan por CONFIG. Cada app las define; el motor no cambia.
 */

/** Cuánta XP otorga un evento: número fijo o función del evento. */
export type XpRule = number | ((event: GameEvent) => number);

export interface GamificationConfig {
  /** eventType → XP. Los tipos no listados dan 0. */
  xp: Record<string, XpRule>;
  /**
   * eventType → línea(s) de racha que "activa" ese evento (p. ej.
   * "lesson.completed" → ["study"]). Un evento marca su `day` como activo.
   */
  streakLines?: Record<string, string[]>;
  /** eventType → contador(es) a incrementar (default: el propio type). */
  counters?: Record<string, string[]>;
  badges?: BadgeDef[];
  quests?: QuestDef[];
  curve?: XpCurve;
}

export function initialState(freezes = 0): PlayerState {
  return {
    xp: 0,
    level: 1,
    counters: {},
    streaks: {},
    activeDays: {},
    badges: [],
    quests: {},
    freezes,
  };
}

function xpFor(rule: XpRule | undefined, event: GameEvent): number {
  if (rule == null) {
    return 0;
  }
  return typeof rule === "function" ? rule(event) : rule;
}

function dayOf(event: GameEvent): DayKey {
  return event.day ?? dayKeyUTC(event.at);
}

/**
 * Procesa una tanda de eventos sobre un estado. Devuelve el estado nuevo y las
 * recompensas agregadas. NO muta el estado de entrada (copia defensiva).
 */
export function applyEvents(
  state: PlayerState,
  events: readonly GameEvent[],
  config: GamificationConfig,
): { state: PlayerState; awards: Awards } {
  const curve = config.curve ?? DEFAULT_CURVE;
  const next: PlayerState = {
    ...state,
    counters: { ...state.counters },
    streaks: { ...state.streaks },
    activeDays: { ...state.activeDays },
    badges: [...state.badges],
    quests: { ...state.quests },
  };

  const fromLevel = state.level;
  let gainedXp = 0;
  const touchedLines = new Set<string>();

  for (const event of events) {
    // XP.
    const gained = xpFor(config.xp[event.type], event);
    gainedXp += gained;

    // Contadores.
    const counterKeys = config.counters?.[event.type] ?? [event.type];
    for (const key of counterKeys) {
      next.counters[key] = (next.counters[key] ?? 0) + 1;
    }

    // Días activos por línea de racha.
    const lines = config.streakLines?.[event.type] ?? [];
    const day = dayOf(event);
    for (const line of lines) {
      const arr = next.activeDays[line] ? [...next.activeDays[line]] : [];
      if (!arr.includes(day)) {
        arr.push(day);
      }
      next.activeDays[line] = arr;
      touchedLines.add(line);
    }

    // Quests.
    for (const quest of config.quests ?? []) {
      const prog = applyEventToQuest(quest, next.quests[quest.id], event, day);
      next.quests[quest.id] = prog;
    }
  }

  next.xp = state.xp + gainedXp;

  // Recalcula rachas de las líneas tocadas (ancladas al día más reciente visto).
  const streakAwards: Record<string, StreakState> = {};
  for (const line of touchedLines) {
    const days = next.activeDays[line] ?? [];
    const today = days.reduce((max, d) => (d > max ? d : max), days[0] ?? "");
    const streak = computeStreak(days, today, { freezes: next.freezes });
    streak.lastDay = today;
    next.streaks[line] = streak;
    streakAwards[line] = streak;
  }

  // Quests recién completadas → bonus XP (una sola vez; marcado por `awarded`).
  const questsCompleted: string[] = [];
  for (const quest of config.quests ?? []) {
    const prog = next.quests[quest.id];
    const was = state.quests[quest.id]?.completed ?? false;
    if (prog?.completed && !was) {
      questsCompleted.push(quest.id);
      if (quest.bonusXp) {
        next.xp += quest.bonusXp;
      }
    }
  }

  // Nivel tras XP total.
  next.level = levelForXp(next.xp, curve);

  // Badges (evaluados sobre el estado ya actualizado).
  const earned = newlyEarnedBadges(next, config.badges ?? []);
  for (const badge of earned) {
    next.badges.push(badge.id);
    if (badge.bonusXp) {
      next.xp += badge.bonusXp;
    }
  }
  // Un badge con bonus puede subir nivel de nuevo.
  next.level = levelForXp(next.xp, curve);

  const awards: Awards = {
    xp: next.xp - state.xp,
    badges: earned.map((b) => b.id),
    questsCompleted,
    streaks: streakAwards,
  };
  if (next.level > fromLevel) {
    awards.leveledUp = { from: fromLevel, to: next.level };
  }
  return { state: next, awards };
}
